import { useQuery } from "@tanstack/react-query";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import clsx from "clsx";
import { api } from "../api/client";
import {
  DailyMetric,
  InstagramAccount,
  InteractionType,
  MetricsSummary,
} from "../api/types";
import FollowerGrowthChart from "../components/charts/FollowerGrowthChart";
import DailyActionsChart from "../components/charts/DailyActionsChart";
import FollowDeltaChart from "../components/charts/FollowDeltaChart";
import InteractionDetailDrawer from "../components/InteractionDetailDrawer";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";
import Avatar from "../components/ui/Avatar";
import StatusDot from "../components/ui/StatusDot";
import { SkeletonTiles, Skeleton } from "../components/ui/Skeleton";
import {
  IconAccounts,
  IconArrowUp,
  IconChat,
  IconComment,
  IconDashboard,
  IconEye,
  IconHeart,
  IconUserMinus,
  IconUserPlus,
  IconUsers,
} from "../components/ui/Icons";

function successCount(row?: { success: number; failed: number; skipped: number }): number {
  if (!row) return 0;
  return row.success;
}

interface DrawerState {
  type: InteractionType;
  title: string;
  /** ISO timestamp - earliest createdAt to include. */
  since: string;
}

export default function Dashboard() {
  const { t } = useTranslation();
  const accountsQuery = useQuery({
    queryKey: ["instagram-accounts"],
    queryFn: async () => {
      const { data } = await api.get<{ accounts: InstagramAccount[] }>(
        "/api/instagram-accounts"
      );
      return data.accounts;
    },
  });

  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedAccountId && accountsQuery.data?.length) {
      setSelectedAccountId(accountsQuery.data[0].id);
    }
  }, [accountsQuery.data, selectedAccountId]);

  const summaryQuery = useQuery({
    queryKey: ["metrics-summary", selectedAccountId],
    queryFn: async () => {
      const { data } = await api.get<MetricsSummary>("/api/metrics/summary", {
        params: { igAccountId: selectedAccountId },
      });
      return data;
    },
    enabled: !!selectedAccountId,
  });

  const dailyQuery = useQuery({
    queryKey: ["daily-metrics", selectedAccountId],
    queryFn: async () => {
      const { data } = await api.get<{ metrics: DailyMetric[] }>("/api/metrics/daily", {
        params: { igAccountId: selectedAccountId },
      });
      return data.metrics;
    },
    enabled: !!selectedAccountId,
  });

  const accounts = accountsQuery.data || [];
  const summary = summaryQuery.data;
  const metrics = dailyQuery.data || [];
  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === selectedAccountId),
    [accounts, selectedAccountId]
  );

  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const openDrawer = (type: InteractionType, hours: number, label: string): void => {
    setDrawer({
      type,
      title: label,
      since: new Date(Date.now() - hours * 3600 * 1000).toISOString(),
    });
  };

  if (accountsQuery.isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow={t("dashboard.section.overview")}
          title={t("dashboard.title")}
          subtitle={t("dashboard.subtitle")}
          icon={<IconDashboard />}
        />
        <Skeleton className="h-44 w-full" />
        <SkeletonTiles count={6} />
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow={t("dashboard.section.overview")}
          title={t("dashboard.title")}
          subtitle={t("dashboard.subtitle")}
          icon={<IconDashboard />}
        />
        <EmptyState
          icon={<IconAccounts size={26} />}
          title={t("dashboard.empty.title")}
          description={t("dashboard.empty.subtitle")}
          action={
            <Link to="/instagram-accounts/new" className="btn-primary px-5 py-2.5">
              {t("dashboard.empty.cta")}
            </Link>
          }
        />
      </div>
    );
  }

  // Compute the 30-day follower delta from the metrics series for the hero card.
  const followerDelta = (() => {
    if (metrics.length < 2) return 0;
    return metrics[metrics.length - 1].followers - metrics[0].followers;
  })();

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("dashboard.section.overview")}
        title={t("dashboard.title")}
        subtitle={t("dashboard.subtitle")}
        icon={<IconDashboard />}
        actions={
          <AccountSwitcher
            accounts={accounts}
            selectedId={selectedAccountId}
            onSelect={setSelectedAccountId}
          />
        }
      />

      <FollowerHero
        account={selectedAccount}
        delta={followerDelta}
        sparkline={metrics.slice(-14).map((m) => ({ v: m.followers }))}
      />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
        <KpiTile
          icon={<IconEye />}
          tone="sky"
          label={t("dashboard.tile.storyViews24h")}
          value={successCount(summary?.last24h.view_story)}
          onClick={() => openDrawer("view_story", 24, t("dashboard.tile.storyViews24h"))}
        />
        <KpiTile
          icon={<IconHeart />}
          tone="rose"
          label={t("dashboard.tile.storyLikes24h")}
          value={successCount(summary?.last24h.like_story)}
          onClick={() =>
            openDrawer("like_story", 24, t("dashboard.tile.storyLikes24h"))
          }
        />
        <KpiTile
          icon={<IconHeart />}
          tone="rose"
          label={t("dashboard.tile.postLikes24h")}
          value={successCount(summary?.last24h.like_post)}
          onClick={() =>
            openDrawer("like_post", 24, t("dashboard.tile.postLikes24h"))
          }
        />
        <KpiTile
          icon={<IconChat />}
          tone="emerald"
          label={t("dashboard.tile.dms24h")}
          value={successCount(summary?.last24h.send_dm)}
          onClick={() => openDrawer("send_dm", 24, t("dashboard.tile.dms24h"))}
        />
        <KpiTile
          icon={<IconUserPlus />}
          tone="violet"
          label={t("dashboard.tile.follows24h")}
          value={successCount(summary?.last24h.follow)}
          onClick={() => openDrawer("follow", 24, t("dashboard.tile.follows24h"))}
        />
        <KpiTile
          icon={<IconUserMinus />}
          tone="slate"
          label={t("dashboard.tile.unfollows24h")}
          value={successCount(summary?.last24h.unfollow)}
          onClick={() => openDrawer("unfollow", 24, t("dashboard.tile.unfollows24h"))}
        />
        <KpiTile
          icon={<IconComment />}
          tone="amber"
          label={t("dashboard.tile.comments24h")}
          value={successCount(summary?.last24h.comment)}
          onClick={() => openDrawer("comment", 24, t("dashboard.tile.comments24h"))}
        />
        <KpiTile
          icon={<IconEye />}
          tone="sky"
          label={t("dashboard.tile.storyViews7d")}
          value={successCount(summary?.last7d.view_story)}
          onClick={() =>
            openDrawer("view_story", 24 * 7, t("dashboard.tile.storyViews7d"))
          }
        />
        <KpiTile
          icon={<IconChat />}
          tone="emerald"
          label={t("dashboard.tile.dms7d")}
          value={successCount(summary?.last7d.send_dm)}
          onClick={() => openDrawer("send_dm", 24 * 7, t("dashboard.tile.dms7d"))}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
        <ChartCard
          eyebrow={t("dashboard.section.trends")}
          title={t("dashboard.followerGrowth")}
        >
          {metrics.length > 0 ? (
            <FollowerGrowthChart data={metrics} />
          ) : (
            <ChartEmpty>{t("dashboard.noSnapshotsYet")}</ChartEmpty>
          )}
        </ChartCard>
        <ChartCard
          eyebrow={t("dashboard.section.trends")}
          title={t("dashboard.dailyActions")}
        >
          {metrics.length > 0 ? (
            <DailyActionsChart data={metrics} />
          ) : (
            <ChartEmpty>{t("dashboard.noDataYet")}</ChartEmpty>
          )}
        </ChartCard>
      </div>

      <ChartCard
        eyebrow={t("dashboard.section.balance")}
        title={t("dashboard.followDelta")}
        description={t("dashboard.followDeltaHelp")}
      >
        {metrics.length > 0 ? (
          <FollowDeltaChart data={metrics} />
        ) : (
          <ChartEmpty>{t("dashboard.noDataYet")}</ChartEmpty>
        )}
      </ChartCard>

      <div className="space-y-1 text-xs text-slate-500">
        <p>{t("dashboard.tilesHint")}</p>
        <p>{t("dashboard.scheduleHint")}</p>
      </div>

      {drawer && selectedAccountId && (
        <InteractionDetailDrawer
          open
          igAccountId={selectedAccountId}
          type={drawer.type}
          title={drawer.title}
          since={drawer.since}
          onClose={() => setDrawer(null)}
        />
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Account switcher
 *  - Segmented control (chips) when there are ≤ 3 accounts: instant tap-switch.
 *  - Native <select> when there are more: scalable and screen-reader friendly.
 * ------------------------------------------------------------------------ */

interface AccountSwitcherProps {
  accounts: InstagramAccount[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function AccountSwitcher({ accounts, selectedId, onSelect }: AccountSwitcherProps): JSX.Element {
  const { t } = useTranslation();
  if (accounts.length <= 3) {
    return (
      <div className="flex w-full items-center gap-1.5 rounded-xl border border-slate-800/80 bg-slate-900/50 p-1 sm:w-auto">
        {accounts.map((a) => {
          const active = a.id === selectedId;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => onSelect(a.id)}
              className={clsx(
                "flex flex-1 items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-medium transition sm:flex-initial",
                active
                  ? "bg-slate-800 text-white shadow-inner shadow-black/20"
                  : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
              )}
              aria-pressed={active}
            >
              <Avatar name={a.igUsername} size="xs" />
              <span className="truncate">@{a.igUsername}</span>
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <label className="flex w-full items-center gap-2 sm:w-auto">
      <span className="sr-only">{t("dashboard.selectAccount")}</span>
      <select
        className="input sm:w-56"
        value={selectedId || ""}
        onChange={(e) => onSelect(e.target.value)}
      >
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>
            @{a.igUsername}
          </option>
        ))}
      </select>
    </label>
  );
}

/* --------------------------------------------------------------------------
 *  Follower hero card
 *  Big number for the selected account + 30-day delta + small sparkline so
 *  the user can see the trend before they read any tile.
 * ------------------------------------------------------------------------ */

function FollowerHero({
  account,
  delta,
  sparkline,
}: {
  account: InstagramAccount | undefined;
  delta: number;
  sparkline: Array<{ v: number }>;
}): JSX.Element {
  const { t } = useTranslation();
  const followers = account?.followerCount ?? 0;
  const live = account?.status === "active";
  const positive = delta > 0;

  return (
    <section
      aria-labelledby="hero-follower-title"
      className="card-feature flex flex-col gap-5 md:flex-row md:items-center md:justify-between"
    >
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_right,_rgba(209,56,255,0.15),_transparent_55%)]" />
      <div className="min-w-0 space-y-2.5">
        <div className="flex items-center gap-2">
          {account && <Avatar name={account.igUsername} size="md" variant="instagram" />}
          <div className="min-w-0">
            <p id="hero-follower-title" className="text-xs uppercase tracking-wider text-brand-200">
              {t("dashboard.hero.eyebrow")}
            </p>
            <p className="truncate text-sm text-slate-400">
              {account ? `@${account.igUsername}` : t("common.dash")}
            </p>
          </div>
          {live && (
            <span className="pill ml-1 bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30">
              <StatusDot tone="success" pulse />
              {t("dashboard.hero.live")}
            </span>
          )}
        </div>
        <div className="flex items-end gap-3">
          <span className="text-gradient-brand text-5xl font-bold tabular-nums tracking-tight sm:text-6xl">
            {followers.toLocaleString()}
          </span>
          <span className="mb-1.5 text-sm text-slate-400">
            {t("dashboard.tile.followingCount", {
              count: account?.followingCount ?? 0,
            })}
          </span>
        </div>
        <div
          className={clsx(
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
            positive
              ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30"
              : "bg-slate-500/15 text-slate-400 ring-1 ring-slate-500/20"
          )}
        >
          {positive && <IconArrowUp size={12} />}
          {positive
            ? t("dashboard.hero.deltaPositive", { delta: delta.toLocaleString() })
            : t("dashboard.hero.deltaNeutral")}
        </div>
      </div>
      <div className="h-24 w-full max-w-sm md:h-28 md:w-72">
        {sparkline.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkline} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="heroSpark" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#e26aff" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#d138ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke="#e26aff"
                strokeWidth={2.5}
                fill="url(#heroSpark)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-end text-xs text-slate-500">
            <IconUsers className="text-slate-700" />
          </div>
        )}
      </div>
    </section>
  );
}

/* --------------------------------------------------------------------------
 *  KPI tile (with icon + accent tone)
 * ------------------------------------------------------------------------ */

type Tone = "sky" | "violet" | "emerald" | "rose" | "amber" | "slate";

const TONES: Record<Tone, { iconBg: string; iconText: string }> = {
  sky: { iconBg: "bg-sky-500/15", iconText: "text-sky-300" },
  violet: { iconBg: "bg-violet-500/15", iconText: "text-violet-300" },
  emerald: { iconBg: "bg-emerald-500/15", iconText: "text-emerald-300" },
  rose: { iconBg: "bg-rose-500/15", iconText: "text-rose-300" },
  amber: { iconBg: "bg-amber-500/15", iconText: "text-amber-300" },
  slate: { iconBg: "bg-slate-500/15", iconText: "text-slate-300" },
};

interface KpiTileProps {
  icon: ReactNode;
  label: string;
  value: number;
  tone?: Tone;
  onClick?: () => void;
}

function KpiTile({ icon, label, value, tone = "sky", onClick }: KpiTileProps): JSX.Element {
  const interactive = !!onClick;
  const t = TONES[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      className={clsx("kpi-tile", !interactive && "kpi-tile-disabled")}
    >
      <div className="flex items-center gap-2">
        <span
          className={clsx(
            "inline-flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-white/5",
            t.iconBg,
            t.iconText
          )}
        >
          {icon}
        </span>
        <span className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
          {label}
        </span>
      </div>
      <div className="mt-3 text-2xl font-semibold tabular-nums text-white sm:text-3xl">
        {value.toLocaleString()}
      </div>
    </button>
  );
}

/* --------------------------------------------------------------------------
 *  Chart card wrapper (eyebrow + title + description)
 * ------------------------------------------------------------------------ */

function ChartCard({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="card">
      <header className="mb-4 space-y-1">
        {eyebrow && <p className="section-eyebrow">{eyebrow}</p>}
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        {description && <p className="text-xs text-slate-400">{description}</p>}
      </header>
      {children}
    </section>
  );
}

function ChartEmpty({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-slate-800 bg-slate-950/40 text-sm text-slate-500">
      {children}
    </div>
  );
}
